import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, FileText, Shield, DollarSign, Gavel, Building, Phone, ExternalLink, CheckCircle, AlertCircle, Paintbrush, Hammer, Trees, Camera, Sparkles, Droplet, Star } from "lucide-react";
import AgentRequestModal from "../components/AgentRequestModal";
import ServiceProviders from "../components/ServiceProviders";
import ServiceRatingDialog from "../components/ServiceRatingDialog";
import { serviceProviders } from "../data/serviceProviders";

const SellerResources = ({ user }) => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState(null);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [ratingProvider, setRatingProvider] = useState({ name: '', category: '' });

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      <Helmet>
        <title>FSBO Seller Resources | Free Tools & Guides for DFW Home Sellers | Its-Sold.com</title>
        <meta name="description" content="Free resources for FSBO sellers in Dallas-Fort Worth. Download contracts, find service providers, get expert tips to sell your home without an agent." />
        <link rel="canonical" href="https://its-sold.com/seller-resources" />
      </Helmet>
      
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={() => navigate('/search')} variant="ghost">Browse Listings</Button>
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="bg-orange-600 hover:bg-orange-700">
                  Dashboard
                </Button>
              ) : (
                <Button onClick={() => navigate('/')} className="bg-orange-600 hover:bg-orange-700">
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            FSBO Seller Resources
          </h1>
          <p className="text-xl text-gray-700 mb-3">
            Professional Services to Help You Sell Successfully
          </p>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            You're saving thousands by going For Sale By Owner - but you don't have to do it alone. 
            These are the same professional services real estate agents coordinate behind the scenes. 
            We've partnered with trusted providers to help you through every step.
          </p>
        </div>

        {/* Value Proposition Banner */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 mb-12 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Understanding What Real Estate Agents & Brokers Do
            </h2>
            <p className="text-lg mb-4">
              Professional real estate agents and brokers coordinate numerous services and handle complex processes to earn their commission. 
              As an FSBO seller, you'll need these same services - but you control the process and save money.
            </p>
            <p className="text-base italic">
              By the end of your FSBO journey, you'll have a deep appreciation for the work real estate professionals do. 
              And if you decide professional representation is right for you, we're here to help connect you with experienced licensed agents and brokers in the Dallas-Fort Worth area.
            </p>
          </div>
        </div>

        {/* Essential Services */}
        <div className="space-y-6">
          {/* Title Companies */}
          <Card className="p-6 border-2 border-blue-200">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleSection('title')}
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-8 h-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Title Companies</h3>
                  <p className="text-gray-600 mb-2">
                    <strong>What Agents Do:</strong> Coordinate title searches, ensure clean title transfer, handle escrow
                  </p>
                  <p className="text-sm text-gray-500">
                    Essential for legal property transfer • Protects buyers and sellers • Handles closing
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {expandedSection === 'title' ? '−' : '+'}
              </Button>
            </div>
            
            {expandedSection === 'title' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-900">
                    <strong>Why You Need This:</strong> A title company ensures there are no liens, encumbrances, or ownership disputes. 
                    They handle the legal transfer of property and hold funds in escrow. This is non-negotiable - every home sale needs a title company.
                  </p>
                </div>

                <h4 className="font-bold text-gray-900 mb-4">Recommended DFW Title Companies</h4>
                <div className="space-y-4">
                  {serviceProviders.titleCompanies.slice(0, 8).map((provider, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-bold text-gray-900">{provider.name}</h5>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{provider.description}</p>
                      {provider.highlights && (
                        <div className="mb-2 space-y-1">
                          {provider.highlights.map((highlight, hIdx) => (
                            <div key={hIdx} className="flex items-center gap-2 text-xs text-gray-600">
                              <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                              <span>{highlight}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <a 
                        href={provider.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:underline text-sm"
                      >
                        Visit Website <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-blue-900">
                    <strong>Agent Value:</strong> Experienced agents have relationships with title companies and know which ones process FSBO transactions smoothly. 
                    They handle all communication and ensure deadlines are met.
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Real Estate Attorneys */}
          <Card className="p-6 border-2 border-purple-200">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleSection('attorney')}
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gavel className="w-8 h-8 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Real Estate Attorney</h3>
                  <p className="text-gray-600 mb-2">
                    <strong>What Agents Do:</strong> Review contracts, ensure legal compliance, protect your interests
                  </p>
                  <p className="text-sm text-gray-500">
                    Contract review • Legal protection • Dispute resolution
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {expandedSection === 'attorney' ? '−' : '+'}
              </Button>
            </div>
            
            {expandedSection === 'attorney' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-900">
                    <strong>Why You Need This:</strong> Texas real estate law is complex. An attorney reviews your sales contract, 
                    ensures disclosures are complete, and protects you from legal liability. Most agents work with attorneys regularly 
                    to ensure every transaction is legally sound.
                  </p>
                </div>

                <h4 className="font-bold text-gray-900 mb-4">Recommended DFW Real Estate Attorneys</h4>
                <div className="space-y-3 mb-6">
                  {serviceProviders.attorneys.map((provider, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-bold text-gray-900 text-sm">{provider.name}</h5>
                          <p className="text-xs text-gray-700 mt-1">{provider.description}</p>
                          {provider.highlights && (
                            <div className="mt-1 flex flex-wrap gap-2">
                              {provider.highlights.map((highlight, hIdx) => (
                                <span key={hIdx} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{highlight}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2">
                        <a 
                          href={provider.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:underline text-xs"
                        >
                          Visit Website <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                <h4 className="font-bold text-gray-900 mb-4">When to Hire an Attorney</h4>
                <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Contract Review:</strong> Before signing any purchase agreement ($300-500)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Complex Situations:</strong> Divorce, estate sales, seller financing ($500-1000)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Disputes:</strong> If buyer threatens to back out or disputes arise ($1000+)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-900">
                    <strong>Agent Value:</strong> Experienced real estate agents know when attorney involvement is necessary and have 
                    trusted referral networks. They can often resolve issues that would otherwise require legal intervention.
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Texas Sales Contracts */}
          <Card className="p-6 border-2 border-green-200">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleSection('contracts')}
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-8 h-8 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">TX Sales Contracts</h3>
                  <p className="text-gray-600 mb-2">
                    <strong>What Agents Do:</strong> Prepare legally compliant contracts, negotiate terms, manage paperwork
                  </p>
                  <p className="text-sm text-gray-500">
                    Standard forms • Legal protection • TREC approved
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {expandedSection === 'contracts' ? '−' : '+'}
              </Button>
            </div>
            
            {expandedSection === 'contracts' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-900">
                    <strong>Critical Information:</strong> Texas has specific legal requirements for real estate contracts. 
                    Agents are trained and licensed to prepare these contracts correctly. As an FSBO seller, you MUST use 
                    legally compliant forms or risk the deal falling apart or facing legal issues.
                  </p>
                </div>

                <h4 className="font-bold text-gray-900 mb-4">Official Texas Contract Resources</h4>
                <div className="space-y-4">
                  <div className="bg-white border-2 border-green-300 rounded-lg p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h5 className="font-bold text-gray-900 text-lg">Texas Real Estate Commission (TREC) Forms</h5>
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-semibold">OFFICIAL</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      TREC provides free, legally approved forms for Texas real estate transactions. These are the SAME forms 
                      licensed agents use.
                    </p>
                    <ul className="text-sm text-gray-700 space-y-2 mb-4">
                      <li>✓ One to Four Family Residential Contract (Resale)</li>
                      <li>✓ Seller's Disclosure Notice</li>
                      <li>✓ Addendum for Property Subject to Mandatory Membership</li>
                      <li>✓ Addendum for "Back-Up" Contract</li>
                    </ul>
                    <a 
                      href="https://www.trec.texas.gov/forms/forms-residential-sale" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                    >
                      Download TREC Forms <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>

                  <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h5 className="font-bold text-orange-900 mb-2">⚠️ Important Warning</h5>
                        <p className="text-sm text-orange-800 mb-3">
                          While TREC forms are free and legal to use, <strong>understanding and filling them out correctly is complex</strong>. 
                          Real estate agents spend years learning how to properly complete these contracts.
                        </p>
                        <p className="text-sm text-orange-800 font-semibold">
                          We STRONGLY recommend having a real estate attorney review any contract before signing, even if you use TREC forms. 
                          A $300-500 attorney review is cheap insurance on a $300,000+ transaction.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-green-900">
                    <strong>Agent Value:</strong> Licensed agents complete hundreds of contracts during their careers. They know every 
                    blank, checkbox, and contingency. They negotiate terms to protect you and ensure deadlines are met. This expertise 
                    is a major part of what you're paying commission for.
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Mortgage & Financing */}
          <Card className="p-6 border-2 border-indigo-200">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleSection('mortgage')}
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building className="w-8 h-8 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Mortgage & Financing Help</h3>
                  <p className="text-gray-600 mb-2">
                    <strong>What Agents Do:</strong> Connect buyers with lenders, verify pre-approvals, ensure financing closes
                  </p>
                  <p className="text-sm text-gray-500">
                    Buyer pre-approval verification • Lender coordination • Closing timeline management
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {expandedSection === 'mortgage' ? '−' : '+'}
              </Button>
            </div>
            
            {expandedSection === 'mortgage' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-900">
                    <strong>Why This Matters:</strong> Most buyers need financing. Agents verify pre-approvals, work with lenders 
                    to keep deals on track, and know warning signs of financing issues. A deal can fall apart 3 days before closing 
                    if the buyer's financing isn't solid.
                  </p>
                </div>

                <h4 className="font-bold text-gray-900 mb-4">FSBO Seller Tips</h4>
                <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                  <ul className="space-y-3 text-sm text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Require Pre-Approval:</strong> Don't accept offers without a pre-approval letter from a real lender (not just pre-qualification)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Call the Lender:</strong> Yes, you can call the lender on the pre-approval letter to verify it's legitimate</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Cash Buyers:</strong> Ask for proof of funds (bank statement showing they have the cash)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <p className="text-sm text-indigo-900">
                    <strong>Agent Value:</strong> Agents have relationships with multiple lenders and can spot red flags in pre-approvals. 
                    They follow up throughout the loan process and catch issues early. This coordination alone saves deals that would 
                    otherwise fall through.
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Home Insurance */}
          <Card className="p-6 border-2 border-red-200">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleSection('insurance')}
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Home Insurance Providers</h3>
                  <p className="text-gray-600 mb-2">
                    <strong>What Agents Do:</strong> Remind buyers about insurance requirements, connect them with agents
                  </p>
                  <p className="text-sm text-gray-500">
                    Required for closing • Protects buyer's investment • Lender requirement
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {expandedSection === 'insurance' ? '−' : '+'}
              </Button>
            </div>
            
            {expandedSection === 'insurance' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">
                  The buyer needs homeowners insurance before closing. As the seller, this isn't your direct responsibility, 
                  but reminding buyers about this requirement keeps the deal on track.
                </p>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-900">
                    <strong>Agent Value:</strong> Agents have insurance agent referrals and remind buyers about deadlines. 
                    They ensure insurance is secured before closing to prevent last-minute delays.
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Free Valuation Tool CTA for Sellers */}
        <div className="mt-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            What's Your Home Really Worth?
          </h2>
          <p className="text-lg mb-6 max-w-3xl mx-auto">
            Before you list, know your home's true market value! Get FREE instant valuations, access official tax records, 
            and see what similar DFW homes are selling for. Price it right to sell fast!
          </p>
          <Button
            onClick={() => navigate('/home-valuation')}
            className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6 mb-4"
          >
            <DollarSign className="w-5 h-5 mr-2" />
            Get Free Home Valuation Now
          </Button>
          <p className="text-sm text-orange-100">
            Know your value • Price competitively • Sell faster
          </p>
        </div>

        {/* DFW Market Highlights for Sellers */}
        <div className="mt-16 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 sm:p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display' }}>
              Why DFW Homes Are In High Demand
            </h2>
            <p className="text-base sm:text-lg text-gray-700 max-w-3xl mx-auto">
              Selling in one of America's hottest real estate markets. Here's why buyers are flocking to North Texas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-10">
            {/* Economic Powerhouse */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Economic Powerhouse</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• <strong>20+ Fortune 500 HQs</strong> - AT&T, ExxonMobil, American Airlines, Southwest</li>
                    <li>• <strong>#1 Job Growth</strong> - 100,000+ jobs added annually</li>
                    <li>• <strong>Tech Hub</strong> - Growing Silicon Prairie with major tech companies</li>
                    <li>• <strong>Business-Friendly</strong> - No state income tax, lower cost of living</li>
                    <li>• <strong>Diverse Economy</strong> - Finance, healthcare, energy, tech, logistics</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Quality of Life */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Unbeatable Lifestyle</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• <strong>Year-Round Sports</strong> - Cowboys, Mavericks, Rangers, Stars, FC Dallas</li>
                    <li>• <strong>200+ Golf Courses</strong> - TPC Four Seasons, Cowboys Golf Club</li>
                    <li>• <strong>World-Class Arts</strong> - Largest urban arts district in America</li>
                    <li>• <strong>Family Entertainment</strong> - Six Flags, Fort Worth Zoo, museums</li>
                    <li>• <strong>300+ Sunny Days</strong> - Perfect weather for outdoor living</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Education & Healthcare */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Top Schools & Healthcare</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• <strong>Excellent School Districts</strong> - Plano, Frisco, Southlake, Highland Park</li>
                    <li>• <strong>30+ Universities</strong> - UT Dallas, SMU, TCU, UNT</li>
                    <li>• <strong>World-Class Medical</strong> - UT Southwestern, Baylor Scott & White</li>
                    <li>• <strong>Medical City</strong> - Major healthcare innovation hub</li>
                    <li>• <strong>Research Centers</strong> - Leading cancer, heart research</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Infrastructure & Connectivity */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ExternalLink className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Connected to the World</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• <strong>DFW Airport</strong> - 2nd largest in U.S., 260+ destinations</li>
                    <li>• <strong>Love Field</strong> - Convenient domestic travel</li>
                    <li>• <strong>Central Location</strong> - Drive to TX beaches or mountains in 4-5 hours</li>
                    <li>• <strong>Major Highways</strong> - I-35, I-20, I-30, I-45 access</li>
                    <li>• <strong>Fiber Internet</strong> - Gigabit speeds widely available</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Market Stats for Sellers */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white">
            <h3 className="text-2xl font-bold mb-4 text-center">DFW Real Estate Market (2024-2025)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">7.6M</div>
                <div className="text-sm opacity-90">Metro Population</div>
                <div className="text-xs opacity-75 mt-1">4th Largest in U.S.</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">1,000+</div>
                <div className="text-sm opacity-90">People Moving In Daily</div>
                <div className="text-xs opacity-75 mt-1">365K+ Annually</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">$350K</div>
                <div className="text-sm opacity-90">Median Home Price</div>
                <div className="text-xs opacity-75 mt-1">Varies by Area</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">30-45</div>
                <div className="text-sm opacity-90">Avg. Days on Market</div>
                <div className="text-xs opacity-75 mt-1">Fast-Moving Market</div>
              </div>
            </div>
            <p className="text-center text-sm mt-4 opacity-90">
              <strong>High Demand + Growing Population = Great Time to Sell in DFW</strong>
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 italic">
              DFW's explosive growth means strong buyer demand and competitive offers for sellers. Price your home right and it won't last long!
            </p>
          </div>
        </div>

        {/* CTA Section - Professional Services */}
        <div className="mt-16 mb-12">
          <Card className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 border-2 border-orange-200 shadow-lg">
            <div className="p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="inline-block bg-orange-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
                  ⭐ NEW RESOURCES AVAILABLE ⭐
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  Don't Forget About Curb Appeal!
                </h2>
                <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-6">
                  Your home needs to look its absolute best to sell fast and for top dollar. 
                  We've partnered with <span className="font-bold text-orange-600">70+ trusted DFW service providers</span> to help you prepare your home for sale.
                </p>
              </div>

              {/* Quick Categories */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow border border-orange-100">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <h4 className="font-bold text-gray-900 text-sm mb-1">Home Staging</h4>
                  <p className="text-xs text-gray-600">8 Companies</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow border border-orange-100">
                  <Camera className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
                  <h4 className="font-bold text-gray-900 text-sm mb-1">Photography</h4>
                  <p className="text-xs text-gray-600">6 Companies</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow border border-orange-100">
                  <Paintbrush className="w-8 h-8 mx-auto mb-2 text-pink-600" />
                  <h4 className="font-bold text-gray-900 text-sm mb-1">Painting</h4>
                  <p className="text-xs text-gray-600">8 Companies</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow border border-orange-100">
                  <Droplet className="w-8 h-8 mx-auto mb-2 text-teal-600" />
                  <h4 className="font-bold text-gray-900 text-sm mb-1">Cleaning</h4>
                  <p className="text-xs text-gray-600">7 Companies</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow border border-orange-100">
                  <Hammer className="w-8 h-8 mx-auto mb-2 text-amber-600" />
                  <h4 className="font-bold text-gray-900 text-sm mb-1">Handyman</h4>
                  <p className="text-xs text-gray-600">7 Companies</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow border border-orange-100">
                  <Trees className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <h4 className="font-bold text-gray-900 text-sm mb-1">Landscaping</h4>
                  <p className="text-xs text-gray-600">10 Companies</p>
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-white/50 rounded-xl p-6 mb-6">
                <div className="grid md:grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-orange-600 mb-1">10-15%</div>
                    <div className="text-sm text-gray-700">Higher Sale Price</div>
                    <div className="text-xs text-gray-500">With proper staging & prep</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-orange-600 mb-1">50%</div>
                    <div className="text-sm text-gray-700">Faster Sale Time</div>
                    <div className="text-xs text-gray-500">Professional photos & curb appeal</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-orange-600 mb-1">3x</div>
                    <div className="text-sm text-gray-700">More Showings</div>
                    <div className="text-xs text-gray-500">Better first impressions</div>
                  </div>
                </div>
              </div>

              {/* Scroll CTA */}
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-900 mb-4">
                  👇 Scroll down to explore all services and read reviews from other FSBO sellers
                </p>
                <div className="flex justify-center items-center gap-2 text-orange-600 animate-bounce">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  <span className="font-bold">Keep Scrolling for More Resources</span>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* PRIORITY: MLS & Core Services */}
        <div className="mt-16">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-xl mb-12 text-center">
            <h2 className="text-3xl font-bold mb-2">🚀 Get Maximum Exposure for Your Home</h2>
            <p className="text-lg">We've partnered with 100+ DFW pros—earn cash back on referrals via our network</p>
          </div>

          <div className="space-y-8">
            {/* Flat-Fee MLS Brokers - HIGHEST PRIORITY */}
            <ServiceProviders
              services={serviceProviders.mlsBrokers}
              categoryTitle="🏆 Flat-Fee MLS Listing Services (Skip the 6% Commission!)"
              categoryIcon={Home}
              categoryColor="orange"
              description="List on the full MLS (Zillow, Realtor.com, etc.) for a flat fee instead of paying 6% commission. Our broker partners offer packages from $45-$799."
            />

            {/* Home Warranties */}
            <ServiceProviders
              services={serviceProviders.homeWarranties}
              categoryTitle="🛡️ Home Warranty Protection (Protect Your Sale!)"
              categoryIcon={Shield}
              categoryColor="green"
              description="Cover buyer objections and protect yourself with comprehensive home warranties. Starting at $45/month."
            />

            {/* Moving Companies */}
            <ServiceProviders
              services={serviceProviders.movers}
              categoryTitle="🚚 Moving Services (Post-Sale Made Easy)"
              categoryIcon={Building}
              categoryColor="blue"
              description="Planning your move after closing? Get exclusive discounts from our trusted DFW moving partners."
            />
          </div>
        </div>

        {/* Home Preparation Services */}
        <div className="mt-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Playfair Display' }}>
            📸 Home Preparation & Curb Appeal Services
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            First impressions matter! These services help your home look its absolute best to attract buyers and command top dollar.
          </p>

          <div className="space-y-8">
            {/* Staging Services */}
            <ServiceProviders
              services={serviceProviders.staging}
              categoryTitle="Home Staging Services"
              categoryIcon={Sparkles}
              categoryColor="purple"
            />

            {/* Photography */}
            <ServiceProviders
              services={serviceProviders.photography}
              categoryTitle="Photography & Videography"
              categoryIcon={Camera}
              categoryColor="indigo"
            />

            {/* Painting */}
            <ServiceProviders
              services={serviceProviders.painting}
              categoryTitle="Painting Services"
              categoryIcon={Paintbrush}
              categoryColor="pink"
            />

            {/* Cleaning */}
            <ServiceProviders
              services={serviceProviders.cleaning}
              categoryTitle="Cleaning Services"
              categoryIcon={Droplet}
              categoryColor="teal"
            />

            {/* Handyman */}
            <ServiceProviders
              services={serviceProviders.handyman}
              categoryTitle="Handyman Services"
              categoryIcon={Hammer}
              categoryColor="amber"
            />

            {/* Landscaping */}
            <ServiceProviders
              services={serviceProviders.landscaping}
              categoryTitle="Landscaping & Yard Work"
              categoryIcon={Trees}
              categoryColor="green"
            />
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl p-12 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Work With a DFW Real Estate Agent?
          </h2>
          <p className="text-lg text-gray-700 mb-6 max-w-3xl mx-auto">
            By now, you can see why real estate agents and brokers earn their commission - they coordinate all these services, 
            protect your interests, and handle hundreds of details. If you'd like professional real estate representation, 
            we're happy to connect you with experienced licensed agents and brokers in the Dallas-Fort Worth metroplex.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={() => navigate(user ? '/create' : '/')}
              className="bg-orange-600 hover:bg-orange-700 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
            >
              Continue With FSBO
            </Button>
            <Button 
              onClick={() => setShowAgentModal(true)}
              variant="outline"
              className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
            >
              Find DFW Real Estate Agent
            </Button>
          </div>
          <p className="text-sm text-gray-600 mt-6">
            Connect with licensed real estate professionals • DFW agents & brokers • Free consultation
          </p>
          <p className="text-xs text-gray-500 mt-4 max-w-2xl mx-auto italic">
            <strong>Disclaimer:</strong> Its-Sold.com™ is an independent FSBO platform. We are not affiliated with, sponsored by, or representing any real estate brokerage firm. Agent referrals are provided as a free service to help buyers and sellers connect with licensed professionals.
          </p>
        </div>
      </div>

      {/* Agent Request Modal */}
      <AgentRequestModal 
        isOpen={showAgentModal} 
        onClose={() => setShowAgentModal(false)} 
      />

      <ServiceRatingDialog
        open={showRatingDialog}
        onClose={() => setShowRatingDialog(false)}
        providerName={ratingProvider.name}
        providerCategory={ratingProvider.category}
      />

      {/* Floating Rate Service Button */}
      <button
        onClick={() => setShowRatingDialog(true)}
        className="fixed bottom-6 right-6 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 transition-all hover:scale-105 z-50"
      >
        <Star className="w-5 h-5" />
        Rate a Service
      </button>
    </div>
  );
};

export default SellerResources;
